import { VIEW_TAB, type PaneId, type PaneState, type WorkspaceState, type WorkspaceTab } from './types';

/*
  Чистые переходы рабочего пространства. Никаких побочных эффектов: адресная строка, хранение
  и клавиши живут в WorkspaceProvider. Правила, которые здесь держатся всегда:
  - у основного окна есть вкладка вида, её нельзя закрыть, закрепить или унести;
  - закреплённые вкладки стоят первыми в своём окне;
  - пустое второе окно исчезает, фокус уходит в основное;
  - один адрес — одна вкладка: повторное открытие активирует уже открытую.
*/

export type WorkspaceAction =
  | { type: 'setView'; path: string }
  | { type: 'open'; path: string; title: string; pane?: PaneId; background?: boolean }
  | { type: 'activate'; pane: PaneId; id: string }
  | { type: 'close'; pane: PaneId; id: string }
  | { type: 'closeOthers'; pane: PaneId; id: string }
  | { type: 'pin'; id: string; pinned: boolean }
  | { type: 'move'; id: string; to: PaneId; index?: number }
  | { type: 'unsplit' }
  | { type: 'focus'; pane: PaneId }
  | { type: 'setSplit'; ratio: number }
  | { type: 'retitle'; id: string; title: string };

export const SPLIT_MIN = 0.3;
export const SPLIT_MAX = 0.7;

export function initialWorkspace(view: string): WorkspaceState {
  return {
    view,
    tabs: {},
    main: { tabs: [], active: VIEW_TAB },
    side: null,
    focused: 'main',
    split: 0.5,
  };
}

let seq = 0;
function newId() {
  seq += 1;
  return `t${Date.now().toString(36)}${seq.toString(36)}`;
}

function paneOf(s: WorkspaceState, id: string): PaneId | null {
  if (s.main.tabs.includes(id)) return 'main';
  if (s.side?.tabs.includes(id)) return 'side';
  return null;
}

function getPane(s: WorkspaceState, pane: PaneId): PaneState | null {
  return pane === 'main' ? s.main : s.side;
}

function setPane(s: WorkspaceState, pane: PaneId, value: PaneState | null): WorkspaceState {
  if (pane === 'main') return { ...s, main: value ?? s.main };
  return { ...s, side: value };
}

/** Закреплённые — первыми, остальной порядок сохраняется. */
function sortPinned(tabs: string[], all: Record<string, WorkspaceTab>) {
  const pinned = tabs.filter((id) => all[id]?.pinned);
  const rest = tabs.filter((id) => !all[id]?.pinned);
  return [...pinned, ...rest];
}

/** Куда переходит фокус, когда активную вкладку убрали: вправо, иначе влево, иначе к виду. */
function neighbourAfterRemoval(pane: PaneId, before: string[], removed: string): string {
  const i = before.indexOf(removed);
  const after = before.filter((id) => id !== removed);
  if (after.length === 0) return pane === 'main' ? VIEW_TAB : '';
  return after[Math.min(i, after.length - 1)];
}

function removeFromPane(s: WorkspaceState, pane: PaneId, id: string): WorkspaceState {
  const p = getPane(s, pane);
  if (!p) return s;
  const tabs = p.tabs.filter((t) => t !== id);
  const active = p.active === id ? neighbourAfterRemoval(pane, p.tabs, id) : p.active;
  if (pane === 'side' && tabs.length === 0) {
    return { ...s, side: null, focused: 'main' };
  }
  return setPane(s, pane, { tabs, active });
}

function insertAt(list: string[], id: string, index: number | undefined) {
  const next = list.filter((t) => t !== id);
  const at = index === undefined ? next.length : Math.max(0, Math.min(index, next.length));
  next.splice(at, 0, id);
  return next;
}

export function workspaceReducer(s: WorkspaceState, a: WorkspaceAction): WorkspaceState {
  switch (a.type) {
    case 'setView':
      return { ...s, view: a.path, main: { ...s.main, active: VIEW_TAB }, focused: 'main' };

    case 'open': {
      const existing = Object.values(s.tabs).find((t) => t.path === a.path);
      if (existing) {
        if (a.background) return s;
        const where = paneOf(s, existing.id);
        if (!where) return s;
        const p = getPane(s, where)!;
        return { ...setPane(s, where, { ...p, active: existing.id }), focused: where };
      }
      // Окна «рядом» может ещё не быть: тогда оно появляется вместе с первой вкладкой.
      const target: PaneId = a.pane ?? s.focused;
      const id = newId();
      const tabs = { ...s.tabs, [id]: { id, path: a.path, title: a.title, pinned: false } };
      const current = getPane(s, target) ?? { tabs: [], active: '' };
      // Новая вкладка встаёт справа от текущей — как в браузере, а не в конец полосы.
      const anchor = current.tabs.indexOf(current.active);
      const index = anchor >= 0 ? anchor + 1 : current.tabs.length;
      const list = sortPinned(insertAt(current.tabs, id, index), tabs);
      const active = a.background && current.active ? current.active : id;
      const next = setPane({ ...s, tabs }, target, { tabs: list, active });
      return a.background ? next : { ...next, focused: target };
    }

    case 'activate': {
      const p = getPane(s, a.pane);
      if (!p) return s;
      if (a.id !== VIEW_TAB && !p.tabs.includes(a.id)) return s;
      if (a.id === VIEW_TAB && a.pane !== 'main') return s;
      return { ...setPane(s, a.pane, { ...p, active: a.id }), focused: a.pane };
    }

    case 'close': {
      if (a.id === VIEW_TAB || !s.tabs[a.id]) return s;
      const next = removeFromPane(s, a.pane, a.id);
      const tabs = { ...next.tabs };
      delete tabs[a.id];
      return { ...next, tabs };
    }

    case 'closeOthers': {
      const p = getPane(s, a.pane);
      if (!p) return s;
      const doomed = p.tabs.filter((id) => id !== a.id && !s.tabs[id]?.pinned);
      const tabs = { ...s.tabs };
      for (const id of doomed) delete tabs[id];
      const keep = p.tabs.filter((id) => !doomed.includes(id));
      const active = a.id === VIEW_TAB || keep.includes(a.id) ? a.id : p.active;
      return { ...setPane({ ...s, tabs }, a.pane, { tabs: keep, active }), focused: a.pane };
    }

    case 'pin': {
      const t = s.tabs[a.id];
      if (!t) return s;
      const tabs = { ...s.tabs, [a.id]: { ...t, pinned: a.pinned } };
      const where = paneOf(s, a.id);
      if (!where) return { ...s, tabs };
      const p = getPane(s, where)!;
      return setPane({ ...s, tabs }, where, { ...p, tabs: sortPinned(p.tabs, tabs) });
    }

    case 'move': {
      const from = paneOf(s, a.id);
      if (!from) return s;
      if (from === a.to) {
        const p = getPane(s, from)!;
        const list = sortPinned(insertAt(p.tabs, a.id, a.index), s.tabs);
        return { ...setPane(s, from, { tabs: list, active: a.id }), focused: from };
      }
      const removed = removeFromPane(s, from, a.id);
      const target = getPane(removed, a.to) ?? { tabs: [], active: '' };
      const list = sortPinned(insertAt(target.tabs, a.id, a.index), removed.tabs);
      return { ...setPane(removed, a.to, { tabs: list, active: a.id }), focused: a.to };
    }

    case 'unsplit': {
      if (!s.side) return s;
      const tabs = sortPinned([...s.main.tabs, ...s.side.tabs], s.tabs);
      const active = s.focused === 'side' && s.side.active ? s.side.active : s.main.active;
      return { ...s, main: { tabs, active }, side: null, focused: 'main' };
    }

    case 'focus':
      if (a.pane === 'side' && !s.side) return s;
      return s.focused === a.pane ? s : { ...s, focused: a.pane };

    case 'setSplit':
      return { ...s, split: Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, a.ratio)) };

    case 'retitle': {
      const t = s.tabs[a.id];
      if (!t || t.title === a.title) return s;
      return { ...s, tabs: { ...s.tabs, [a.id]: { ...t, title: a.title } } };
    }
  }
}

/** Адрес активной вкладки окна. */
export function paneActivePath(s: WorkspaceState, pane: PaneId): string | null {
  const p = getPane(s, pane);
  if (!p) return null;
  if (p.active === VIEW_TAB) return s.view;
  return s.tabs[p.active]?.path ?? (pane === 'main' ? s.view : null);
}

/** Адрес, который должен стоять в браузере: активная вкладка окна в фокусе. */
export function focusedPath(s: WorkspaceState): string {
  return paneActivePath(s, s.focused) ?? s.view;
}

/** Состояние из хранилища могло устареть или испортиться: берём только то, что сходится. */
export function sanitizeWorkspace(raw: unknown, fallbackView: string): WorkspaceState {
  const base = initialWorkspace(fallbackView);
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<WorkspaceState>;
  const tabs: Record<string, WorkspaceTab> = {};
  for (const t of Object.values(r.tabs ?? {})) {
    if (t && typeof t.id === 'string' && typeof t.path === 'string' && typeof t.title === 'string') {
      tabs[t.id] = { id: t.id, path: t.path, title: t.title, pinned: Boolean(t.pinned) };
    }
  }
  const clean = (p: PaneState | null | undefined, pane: PaneId): PaneState | null => {
    if (!p || !Array.isArray(p.tabs)) return pane === 'main' ? base.main : null;
    const list = sortPinned(
      p.tabs.filter((id) => tabs[id]),
      tabs,
    );
    if (pane === 'side' && list.length === 0) return null;
    const active =
      p.active === VIEW_TAB && pane === 'main'
        ? VIEW_TAB
        : list.includes(p.active)
          ? p.active
          : (list[0] ?? VIEW_TAB);
    return { tabs: list, active };
  };
  const main = clean(r.main, 'main') ?? base.main;
  const side = clean(r.side, 'side');
  // Вкладка не может жить в двух окнах сразу и не должна висеть вне окон.
  const used = new Set([...main.tabs, ...(side?.tabs ?? [])]);
  for (const id of Object.keys(tabs)) if (!used.has(id)) delete tabs[id];
  return {
    view: typeof r.view === 'string' ? r.view : fallbackView,
    tabs,
    main,
    side: side && side.tabs.every((id) => !main.tabs.includes(id)) ? side : null,
    focused: r.focused === 'side' && side ? 'side' : 'main',
    split: typeof r.split === 'number' ? Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, r.split)) : 0.5,
  };
}
